import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User, Twitter, Linkedin, Facebook, Share2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSEO } from "@/hooks/useSEO";
import RelatedArticlesSection from "@/components/RelatedArticlesSection";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  published_at: string;
  featured_image: string | null;
  blog_categories: {
    name: string;
    slug: string;
  } | null;
}

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  // Set SEO metadata - call hook at top level with conditional values
  useSEO({
    title: post ? `${post.title} | Ommni Blog` : 'Blog Post | Ommni',
    description: post?.excerpt || 'Read insights on commerce operations, product management, and retail technology.',
    keywords: post?.blog_categories?.name ? `${post.blog_categories.name}, retail technology, ${post.title}` : 'commerce, retail technology',
    canonical: post ? `https://ommni.io/blog/${post.slug}` : undefined,
    ogImage: post?.featured_image || undefined,
  });

  const fetchPost = async () => {
    const { data, error } = await supabase
      .from("blog_posts")
      .select(`
        *,
        blog_categories (
          name,
          slug
        )
      `)
      .eq("slug", slug)
      .eq("published", true)
      .single();

    if (!error && data) {
      setPost(data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="px-4 pb-24">
          <div className="container mx-auto max-w-4xl">
            <p className="text-center text-muted-foreground">Loading...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!post) {
    return (
      <PageLayout>
        <div className="px-4 pb-24">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
            <p className="text-muted-foreground mb-8">
              The blog post you're looking for doesn't exist.
            </p>
            <Link to="/blog">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {post && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt || post.title,
            "image": post.featured_image || "https://ommni.io/assets/ommni-logo.png",
            "datePublished": post.published_at,
            "dateModified": post.published_at,
            "author": {
              "@type": "Organization",
              "name": "Ommni",
              "url": "https://ommni.io"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Ommni",
              "logo": {
                "@type": "ImageObject",
                "url": "https://ommni.io/assets/ommni-logo.png",
                "width": 600,
                "height": 60
              },
              "url": "https://ommni.io"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://ommni.io/blog/${post.slug}`
            },
            "articleSection": post.blog_categories?.name || "Commerce Insights",
            "keywords": post.blog_categories?.name || "commerce, retail technology",
            "articleBody": post.content,
            "wordCount": post.content.split(/\s+/).length,
            "inLanguage": "en-US",
            "isAccessibleForFree": true
          })}
        </script>
      )}
      <div className="px-4 pb-24">
        <article className="container mx-auto max-w-4xl">
          <Link to="/blog">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </Link>

          <Card className="p-8 md:p-12">
            {post.featured_image && (
              <div className="mb-8 -mx-8 md:-mx-12 -mt-8 md:-mt-12">
                <img 
                  src={post.featured_image} 
                  alt={post.title}
                  className="w-full h-64 md:h-96 object-cover"
                />
              </div>
            )}
            
            {post.blog_categories && (
              <Badge className="mb-4">{post.blog_categories.name}</Badge>
            )}
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-xl text-muted-foreground mb-8">
                {post.excerpt}
              </p>
            )}

            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8 pb-8 border-b">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Date(post.published_at).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Ommni Team
              </div>
            </div>

            {/* Social Sharing */}
            <div className="mb-8 pb-8 border-b">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Share2 className="h-4 w-4" />
                  Share:
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      const text = encodeURIComponent(post.title);
                      window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=550,height=420');
                    }}
                  >
                    <Twitter className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=550,height=420');
                    }}
                  >
                    <Linkedin className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=550,height=420');
                    }}
                  >
                    <Facebook className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="blog-content text-lg">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {post.content}
              </ReactMarkdown>
            </div>
          </Card>

          {/* Related Articles */}
          <RelatedArticlesSection 
            currentSlug={post.slug} 
            categorySlug={post.blog_categories?.slug}
          />
        </article>
      </div>
    </PageLayout>
  );
};

export default BlogPost;
